import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GroupedPeriodQuery, MachineStatsState, PeriodQuery, RepairCostsQuery } from "./models/machineStatsModels";
import customerMachineStatsApi from "./api/customerMachineStatsApi";
import customerInstalledStockItemApi from "../shared/api/customerInstalledStockItemApi";

export const getMachineStatus = createAsyncThunk(
  "customerMachineStats/getMachineStatus",
  async (id: string) => {
    return customerMachineStatsApi.getMachineStatus(id);
  }
);

export const getRepairCostsOverview = createAsyncThunk(
  "customerMachineStats/getRepairCostsOverview",
  async ({ id, query }: { id: string, query: RepairCostsQuery }) => {
    return customerMachineStatsApi.getRepairCosts(id, query);
  }
);

export const getCostPerRepair = createAsyncThunk(
  "customerMachineStats/getCostPerRepair",
  async ({ id, query }: { id: string, query: RepairCostsQuery }) => {
    return customerMachineStatsApi.getRepairCosts(id, query);
  }
);

export const getEquipment = createAsyncThunk(
  "customerMachine/getEquipment",
  async (machineId: string) => {
    return customerInstalledStockItemApi.getMachineStockItemStats(machineId);
  }
);

export const getServiceReportHistory = createAsyncThunk(
  "customerMachineStats/getServiceReportHistory",
  async ({ id, query }: { id: string, query: PeriodQuery }) => {
    return customerMachineStatsApi.getServiceReportHistory(id, query);
  }
);

export const getPerformance = createAsyncThunk(
  "customerMachineStats/getPerformance",
  async ({ id, query }: { id: string, query: GroupedPeriodQuery }) => {
    return customerMachineStatsApi.getPerformance(id, query);
  }
);

const initialState: MachineStatsState = {
  machineStatus: undefined,
  machineStatusLoading: false,
  repairCostsOverviewQuery: undefined,
  repairCostsOverview: undefined,
  repairCostsOverviewLoading: false,
  performanceSummaryQuery: undefined,
  performanceSummary: undefined,
  performanceSummaryLoading: false,
  equipment: undefined,
  equipmentLoading: false,
  serviceReportHistoryQuery: undefined,
  serviceReportHistory: undefined,
  serviceReportHistoryLoading: false,
  costPerRepairQuery: undefined,
  costPerRepair: undefined,
  costPerRepairLoading: false,
  loading: false,
  error: undefined
};

const customerMachineStatsSlice = createSlice({
  name: "customerMachineStats",
  initialState: initialState,
  reducers: {
    setLoading: (state, { payload }) => {
      state.loading = payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMachineStatus.pending, (state) => {
        state.machineStatusLoading = true;
      })
      .addCase(getMachineStatus.fulfilled, (state, { payload }) => {
        state.machineStatus = payload;
        state.machineStatusLoading = false;
      })
      .addCase(getMachineStatus.rejected, (state, { error }) => {
        state.error = error;
        state.machineStatusLoading = false;
      });

    builder
      .addCase(getRepairCostsOverview.pending, (state) => {
        state.repairCostsOverviewLoading = true;
      })
      .addCase(getRepairCostsOverview.fulfilled, (state, { payload }) => {
        state.repairCostsOverview = payload;
        state.repairCostsOverviewLoading = false;
      })
      .addCase(getRepairCostsOverview.rejected, (state, { error }) => {
        state.error = error;
        state.repairCostsOverviewLoading = false;
      });

    builder
      .addCase(getCostPerRepair.pending, (state) => {
        state.costPerRepairLoading = true;
      })
      .addCase(getCostPerRepair.fulfilled, (state, { payload }) => {
        state.costPerRepair = payload?.categories;
        state.costPerRepairLoading = false;
      })
      .addCase(getCostPerRepair.rejected, (state, { error }) => {
        state.error = error;
        state.costPerRepairLoading = false;
      });

    builder
      .addCase(getEquipment.pending, (state) => {
        state.equipmentLoading = true;
      })
      .addCase(getEquipment.fulfilled, (state, { payload }) => {
        state.equipment = payload;
        state.equipmentLoading = false;
      })
      .addCase(getEquipment.rejected, (state, { error }) => {
        state.error = error;
        state.equipmentLoading = false;
      });

    builder
      .addCase(getServiceReportHistory.pending, (state) => {
        state.serviceReportHistoryLoading = true;
      })
      .addCase(getServiceReportHistory.fulfilled, (state, { payload }) => {
        state.serviceReportHistory = payload;
        state.serviceReportHistoryLoading = false;
      })
      .addCase(getServiceReportHistory.rejected, (state, { error }) => {
        state.error = error;
        state.serviceReportHistoryLoading = false;
      });

    builder
      .addCase(getPerformance.pending, (state) => {
        state.performanceSummaryLoading = true;
      })
      .addCase(getPerformance.fulfilled, (state, { payload }) => {
        state.performanceSummary = payload;
        state.performanceSummaryLoading = false;
      })
      .addCase(getPerformance.rejected, (state, { error }) => {
        state.error = error;
        state.performanceSummaryLoading = false;
      });
  }
});

export const {
  setLoading
} = customerMachineStatsSlice.actions;

export default customerMachineStatsSlice.reducer;